import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";

export interface BuyerSellerDetails {
  // Common Details
  tcId: string;
  // Seller Details
  sellerLicenseNumber: string;
  sellingOnBehalfOfFlag: string;
  sellerName: string;
  sellerTEId: string;
  sellerAddress1: string;
  sellerAddress2: string;
  sellerAddress3: string;
  sellerPostcode: string;
  sellerTown: string;
  sellerStateOrProvince: string;
  sellerCountryOrArea: string;
  sellerCertifiedOrganizationName: string;
  sellerSCNumber: string; // Assuming array of strings for sellerSCNumber
  // Buyer Details
  buyerLicenseNumber: string;
  buyingOnBehalfOfFlag: string;
  buyerName: string;
  buyerTEId: string;
  buyerAddress1: string;
  buyerAddress2: string;
  buyerAddress3: string;
  buyerPostcode: string;
  buyerTown: string;
  buyerStateOrProvince: string;
  buyerCountryOrArea: string;
  buyerCertifiedOrganizationName: string;
  isBuyerTeCertified: string;
  // Non-Certified Trader
  nonCertifiedTrader: INonCertifiedTrader[];
}

export interface INonCertifiedTrader {
  sellerNonCertifiedTraderName: string;
  sellerNonCertifiedTraderTeId: string;
}

export const buyerSellerDetails: BuyerSellerDetails = {
  // Common Details
  tcId: "",
  // Seller Details
  sellerLicenseNumber: "",
  sellingOnBehalfOfFlag: "Y",
  sellerName: "",
  sellerTEId: "",
  sellerAddress1: "",
  sellerAddress2: "",
  sellerAddress3: "",
  sellerPostcode: "",
  sellerTown: "",
  sellerStateOrProvince: "",
  sellerCountryOrArea: "",
  sellerCertifiedOrganizationName: "",
  sellerSCNumber: "",
  // Buyer Details
  buyerLicenseNumber: "",
  buyingOnBehalfOfFlag: "Y",
  buyerName: "",
  buyerTEId: "",
  buyerAddress1: "",
  buyerAddress2: "",
  buyerAddress3: "",
  buyerPostcode: "",
  buyerTown: "",
  buyerStateOrProvince: "",
  buyerCountryOrArea: "",
  buyerCertifiedOrganizationName: "",
  isBuyerTeCertified: "Y",
  // Non-Certified Trader
  nonCertifiedTrader: [],
};

export const mapBuyerSellerDetailsApiResponseToInitialValues = (data: any) => {
  return {
    tcId: data.tcId || "",
    // Seller Details
    sellerLicenseNumber: data.sellerDetails?.sellerLicenseNo || "",
    sellingOnBehalfOfFlag: data.sellerDetails?.sellingOnBehalfOfFlag || "Y",
    sellerName: data.sellerDetails?.sellerName || "",
    sellerTEId: data.sellerDetails?.sellerTeId || "",
    sellerAddress1: data.sellerDetails?.sellerAddress1 || "",
    sellerAddress2: data.sellerDetails?.sellerAddress2 || "",
    sellerAddress3: data.sellerDetails?.sellerAddress3 || "",
    sellerPostcode: data.sellerDetails?.sellerPostcode || "",
    sellerTown: data.sellerDetails?.sellerTown || "",
    sellerStateOrProvince: data.sellerDetails?.sellerStateOrProvince || "",
    sellerCountryOrArea: data.sellerDetails?.sellerCountryOrArea || "",
    sellerCertifiedOrganizationName: data.sellerDetails?.sellerCertifiedOrganizationName || "",
    sellerSCNumber: data.sellerDetails?.sellerScNo ? data.sellerDetails.sellerScNo[0] : "",
    // Buyer Details
    buyerLicenseNumber: data.buyerDetails?.buyerLicenseNo || "",
    buyingOnBehalfOfFlag: data.buyerDetails?.buyingOnBehalfOfFlag || "Y",
    buyerName: data.buyerDetails?.buyerName || "",
    buyerTEId: data.buyerDetails?.buyerTeId || "",
    buyerAddress1: data.buyerDetails?.buyerAddress1 || "",
    buyerAddress2: data.buyerDetails?.buyerAddress2 || "",
    buyerAddress3: data.buyerDetails?.buyerAddress3 || "",
    buyerPostcode: data.buyerDetails?.buyerPostcode || "",
    buyerTown: data.buyerDetails?.buyerTown || "",
    buyerStateOrProvince: data.buyerDetails?.buyerStateOrProvince || "",
    buyerCountryOrArea: data.buyerDetails?.buyerCountryOrArea || "",
    buyerCertifiedOrganizationName: data.buyerDetails?.buyerCertifiedOrganizationName || "",
    isBuyerTeCertified: (data.buyerDetails?.isBuyerTeCertified && (data.buyerDetails.isBuyerTeCertified === "Y" || data.buyerDetails.isBuyerTeCertified === "N")) 
      ? data.buyerDetails.isBuyerTeCertified 
      : (data.buyerDetails?.buyerTeId && typeof data.buyerDetails.buyerTeId === "string" && data.buyerDetails.buyerTeId.trim() !== "" ? "Y" : "N"),
    // Non-Certified Trader - Filter out empty objects
    nonCertifiedTrader: (data.nonCertifiedTrader || []).filter(
      (trader: any) => {
        // Handle both PascalCase (from API) and camelCase (from frontend)
        const name = trader.SellerNonCertifiedTraderName || trader.sellerNonCertifiedTraderName || "";
        const teId = trader.SellerNonCertifiedTraderTeId || trader.sellerNonCertifiedTraderTeId || "";
        // Only include if at least one field has a non-empty value
        return name.trim() !== "" || teId.trim() !== "";
      }
    ).map((trader: any) => ({
      sellerNonCertifiedTraderName: trader.SellerNonCertifiedTraderName || trader.sellerNonCertifiedTraderName || "",
      sellerNonCertifiedTraderTeId: trader.SellerNonCertifiedTraderTeId || trader.sellerNonCertifiedTraderTeId || ""
    }))
  };
};

export const mapPayload = (jsonData: any, tcId: any, buyerStates?: any, sellerStates?: any) => {
  return {
    tcId: tcId,
    sellerDetails: {
      sellerLicenseNo: jsonData.sellerLicenseNumber || "",
      sellingOnBehalfOfFlag: jsonData.sellingOnBehalfOfFlag || "Y", // Default value as per the payload
      sellerName: jsonData.sellerName || "",
      sellerTeId: jsonData.sellerTEId || "",
      sellerAddress1: jsonData.sellerAddress1 || "",
      sellerAddress2: jsonData.sellerAddress2 || "",
      sellerAddress3: jsonData.sellerAddress3 || "",
      sellerPostcode: jsonData.sellerPostcode || "",
      sellerTown: jsonData.sellerTown || "",
      sellerStateOrProvince: jsonData.sellerStateOrProvince || "",
      sellerCountryOrArea: jsonData.sellerCountryOrArea || "",
      sellerCertifiedOrganizationName: jsonData.sellerCertifiedOrganizationName || "",
      sellerScNo: [jsonData.sellerSCNumber || ""],
      sellerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.sellerCountryOrArea ?? ""))?.label ?? "",
      sellerStateOrProvinceDescription: sellerStates.find((option: any) => option.value === (jsonData.sellerStateOrProvince ?? ""))?.label ?? ""
    },
    buyerDetails: {
      isBuyerTeCertified: jsonData.isBuyerTeCertified || "Y", // Default value as per the payload
      buyerLicenseNo: jsonData.buyerLicenseNumber || "", // Default value as per the payload
      buyingOnBehalfOfFlag: jsonData.buyingOnBehalfOfFlag || "Y", // Default value as per the payload
      buyerName: jsonData.buyerName || "",
      buyerTeId: jsonData.buyerTEId || "",
      buyerAddress1: jsonData.buyerAddress1 || "",
      buyerAddress2: jsonData.buyerAddress2, // Not available in the source JSON
      buyerAddress3: jsonData.buyerAddress3, // Not available in the source JSON
      buyerPostcode: jsonData.buyerPostcode || "",
      buyerTown: jsonData.buyerTown || "",
      buyerStateOrProvince: jsonData.buyerStateOrProvince || "",
      buyerCountryOrArea: jsonData.buyerCountryOrArea || "",
      buyerCertifiedOrganizationName: jsonData.buyerCertifiedOrganizationName || "",
      buyerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.buyerCountryOrArea ?? ""))?.label ?? "",
      buyerStateOrProvinceDescription: buyerStates.find((option: any) => option.value === (jsonData.buyerStateOrProvince ?? ""))?.label ?? ""
    },
    nonCertifiedTrader: jsonData.nonCertifiedTrader || [],
  };
};